import React, { useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import SendEmailInvoiceTab from "./components/SendEmailInvoiceTab";

import { getRegistrationById } from "../../Services/Registration";
import {
  buildRepresentativePayment,
  getAllPaymentsByRegistrationId,
} from "../../Services/Payment";
import { toastError } from "../../utils/toast";
import { useEditParticipantInit } from "./EditParticipant/hooks/useEditParticipantInit";
import { useEditParticipantData } from "./EditParticipant/hooks/useEditParticipantData";
import { useEditParticipantForm } from "./EditParticipant/hooks/useEditParticipantForm";
import { useEditParticipantAuditLoader } from "./EditParticipant/hooks/useEditParticipantAudit";
import { useEditParticipantFormLoader } from "./EditParticipant/hooks/useEditParticipantFormLoader";
import RegistrantInformationSection from "./EditParticipant/components/RegistrantInformationSection";
import SpecialEventsSection from "./EditParticipant/components/SpecialEventsSection";
import RegistrantHistorySection from "./EditParticipant/components/RegistrantHistorySection";
import ProgramRegistrationFeesSection from "./EditParticipant/components/ProgramRegistrationFeesSection";
import RegistrantPaymentSection from "./EditParticipant/components/RegistrantPaymentSection";
import PaymentHistorySection from "./EditParticipant/components/PaymentHistorySection";
import NewPaymentSection from "./EditParticipant/components/NewPaymentSection";
import CcEmailSection from "./EditParticipant/components/CcEmailSection";
import EditParticipantFooter from "./EditParticipant/components/EditParticipantFooter";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../../redux/store";
import {
  setPaymentData,
  setPayments,
  setRegistrationData,
} from "../../redux/states/editParticipant.state";
import {
  selectFirmData,
  selectPaymentData,
  selectRegistrationId,
} from "./EditParticipant/editParticipant.selectors";

export default function EditParticipant (): React.ReactElement {
  const { eventId, registrationId } = useParams<{
    eventId: string;
    registrationId?: string;
  }>();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const openTabFromState = (location.state as { openTab?: number } | null)?.openTab;
  const appliedOpenTabRef = useRef(false);

  useEditParticipantInit({ eventId, registrationId });
  useEditParticipantAuditLoader();
  useEditParticipantFormLoader();

  const {
    visibleTabs,
    canOpenInvoiceTab,
    activeTab,
    setActiveTab,
    eventData,
    registrationData,
  } = useEditParticipantData();

  const paymentData = useSelector(selectPaymentData);
  const firmData = useSelector(selectFirmData);
  const { syncFormFromRegistration } = useEditParticipantForm();
  const resolvedRegistrationId = useSelector(selectRegistrationId);

  useEffect(() => {
    appliedOpenTabRef.current = false;
  }, [eventId, registrationId]);

  useEffect(() => {
    if (appliedOpenTabRef.current || openTabFromState == null) {
      return;
    }
    appliedOpenTabRef.current = true;
    setActiveTab(openTabFromState);
  }, [openTabFromState, setActiveTab, eventId, registrationId]);

  if (!eventId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-lg text-red-600">
          Missing event ID
        </div>
        <button
          onClick={() => navigate("/events-staff")}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Back to Events
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white w-full text-gray-font overflow-y-auto rounded-t-lg">
      <div className="flex bg-accent-Default-dark px-6 py-2.5 gap-4 overflow-x-auto rounded-t-lg">
        {visibleTabs.map((tab, index) => (
          <button
            key={tab}
            onClick={() => {
              if (index === 1 && !canOpenInvoiceTab) {
                toastError("Save the registration before opening the invoice tab.");
                return;
              }
              setActiveTab(index);
            }}
            className={`flex items-center w-52 text-center justify-center font-semibold gap-2 px-4 py-2.5 rounded-md ${activeTab === index
              ? "bg-accent-Default-light text-secondary-dark"
              : "bg-secondary text-white"}`}
          >
            <span>{tab}</span>
          </button>
        ))}
      </div>

      {activeTab === 1 && eventData && registrationData ? (
        <div className="p-4 md:p-6 lg:p-10">
          <SendEmailInvoiceTab
            event={eventData}
            registration={registrationData}
            payment={paymentData}
            firm={firmData}
            onRegistrationRefresh={async () => {
              if (!resolvedRegistrationId) {
                return;
              }
              const refreshed = await getRegistrationById(resolvedRegistrationId);
              if (refreshed) {
                dispatch(setRegistrationData(refreshed));
                syncFormFromRegistration(refreshed);
              }
              const payments = await getAllPaymentsByRegistrationId(
                resolvedRegistrationId,
              );
              dispatch(setPayments(payments));
              dispatch(
                setPaymentData(buildRepresentativePayment(payments) || undefined),
              );
            }}
          />
        </div>
      ) : (
        <>
          <div className="flex flex-col p-4 md:p-6 lg:p-10 gap-6 bg-white">
            <RegistrantInformationSection />
            <SpecialEventsSection />
            <RegistrantHistorySection />
            <ProgramRegistrationFeesSection />
            <RegistrantPaymentSection />
            <NewPaymentSection />
            <CcEmailSection />
            <PaymentHistorySection />
          </div>

          <EditParticipantFooter />
        </>
      )}
    </div>
  );
}
